import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Table,
  Input,
  Button,
} from "reactstrap";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";

import ConfirmModal from "../../../Components/Common/ConfirmModal";
import {
  addSupplier,
  deleteSupplier,
  editSupplier,
  fetchSuppliersList,
} from "../../../slices/thunks";
import CustomPagination from "../../../Components/Common/CustomePagination";
import AddSupplierModal from "../../../Modals/Supplier/AddSupplierModal";
import EditSupplierModal from "../../../Modals/Supplier/EditSupplierModal";
import ViewSupplierModal from "../../../Modals/Supplier/DetailsSupplierModal";
import NF from "../../../assets/images/file.png";

const Supplier = () => {
  const dispatch = useDispatch();

  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debounceSearch, setDebouncedSearch] = useState("");
  const [isAddFormVisible, setIsAddFormVisible] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [supplierIdToDelete, setSupplierIdToDelete] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const selectSupplierListState = (state) => state;

  const supplierSelector = createSelector(selectSupplierListState, (state) => ({
    suppliers: state.Supplier.suppliers,
    loading: state.Supplier.loading,
    error: state.Supplier.error,
    pagination: state.Supplier.pagination,
  }));

  const { suppliers, loading, error, pagination } =
    useSelector(supplierSelector);

  const toggleAddModal = () => setIsAddModalOpen(!isAddModalOpen);
  const toggleConfirmModal = () => setIsConfirmModalOpen(!isConfirmModalOpen);
  const toggleViewModal = () => setIsViewModalOpen(!isViewModalOpen);

  const handleAddSupplier = async (data) => {
    await dispatch(addSupplier(data));
    await dispatch(
      fetchSuppliersList({
        pageNo,
        pageSize,
        sortBy: "createdAt",
        sortOrder: "DESC",
        isPagination: 1,
      })
    );
  };

  const handleRowClick = (supplier) => {
    setSelectedSupplier(supplier);
    toggleViewModal();
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchTerm]);

  useEffect(() => {
    dispatch(
      fetchSuppliersList({
        pageNo,
        pageSize,
        sortBy: "createdAt",
        sortOrder: "DESC",
        isPagination: 1,
        searchTerm: debounceSearch.trim(),
      })
    );
  }, [dispatch, pageNo, pageSize, debounceSearch]);

  const handleEditClick = (supplier) => {
    setSelectedSupplier(supplier);
    setIsEditModalOpen(true);
  };

  const handleUpdateSupplier = async (updatedSupplier) => {
    await dispatch(editSupplier(updatedSupplier));
    await dispatch(
      fetchSuppliersList({
        pageNo,
        pageSize,
        sortBy: "createdAt",
        sortOrder: "DESC",
        isPagination: 1,
      })
    );
  };

  const handleDeleteClick = (id) => {
    setSupplierIdToDelete(id);
    setIsConfirmModalOpen(true);
  };

  const confirmDelete = async () => {
    if (supplierIdToDelete) {
      await dispatch(deleteSupplier(supplierIdToDelete));
      setSupplierIdToDelete(null);
      await dispatch(
        fetchSuppliersList({
          pageNo: 0,
          pageSize,
          sortBy: "createdAt",
          sortOrder: "DESC",
          isPagination: 1,
          searchTerm: debounceSearch.trim(),
        })
      );
    }
  };

  document.title = "Supplier List";

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardHeader className="d-flex align-items-center flex-wrap gap-2">
            <h4 className="card-title mb-0 me-2">Supplier List</h4>

            <div className="search-box ms-3">
              <Input
                type="text"
                className="form-control search"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPageNo(0);
                }}
              />
              <i className="ri-search-line search-icon"></i>
            </div>

            <div className="ms-auto">
              <Button
                color="primary"
                size="sm"
                onClick={toggleAddModal}
                className="btn btn-primary btn-sm"
              >
                {isAddFormVisible ? (
                  <>
                    <i className="ri-arrow-left-line align-bottom me-1"></i>
                    Back to List
                  </>
                ) : (
                  <>
                    <i className="ri-add-line align-bottom me-1"></i>
                    Add Supplier
                  </>
                )}
              </Button>
            </div>
          </CardHeader>

          <CardBody>
            {isAddFormVisible ? (
              <AddSupplierForm onSubmit={() => setIsAddFormVisible(false)} />
            ) : (
              <div className="table-responsive">
                <Table className="table align-middle table-nowrap text-start">
                  <thead className="table-light">
                    <tr>
                      <th>Sr No</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Mobile No</th>
                      <th>Per KM Rate</th>
                      <th>Per Trip Rate</th>
                      <th>Party Type</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      [...Array(pageSize)].map((_, index) => (
                        <tr key={index}>
                          {[...Array(10)].map((__, i) => (
                            <td key={i}>
                              <Skeleton height={25} />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : error ? (
                      <tr>
                        <td colSpan="10" className="text-danger">
                          {error}
                        </td>
                      </tr>
                    ) : suppliers?.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="text-danger text-center">
                          <img
                            src={NF}
                            alt="No File"
                            style={{ width: "500px", marginTop: "10px" }}
                          />
                        </td>
                      </tr>
                    ) : (
                      suppliers.map((item, index) => (
                        <tr
                          key={item.id}
                          onClick={() => handleRowClick(item)}
                          style={{ cursor: "pointer" }}
                        >
                          <td>{pageNo * pageSize + index + 1}</td>
                          <td>{item.name}</td>
                          <td>{item.email}</td>
                          <td>{item.mobileNo}</td>
                          <td>{item.km}</td>
                          <td>{item.perTripRate}</td>
                          <td>{item.partyType}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <button
                                type="button"
                                class="btn btn-sm btn-outline-success btn-icon waves-effect waves-light"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick(item);
                                }}
                              >
                                <i class="ri-edit-2-line"></i>
                              </button>

                              <button
                                type="button"
                                class="btn btn-sm btn-outline-danger btn-icon waves-effect waves-light"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(item?.id);
                                }}
                              >
                                <i class="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )}

            {!isAddFormVisible && (
              <CustomPagination
                currentPage={pagination?.currentPage}
                totalPages={pagination?.totalPages}
                onPageChange={(page) => setPageNo(page)}
                pageSize={pageSize}
                setPageSize={setPageSize}
                setPageNo={setPageNo}
              />
            )}
          </CardBody>
        </Card>

        {isEditModalOpen && (
          <EditSupplierModal
            isOpen={isEditModalOpen}
            toggle={() => setIsEditModalOpen(false)}
            supplier={selectedSupplier}
            onSubmit={handleUpdateSupplier}
          />
        )}

        <ViewSupplierModal
          isOpen={isViewModalOpen}
          toggle={toggleViewModal}
          supplier={selectedSupplier}
        />
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          toggle={toggleConfirmModal}
          title="Delete Supplier"
          message="Are you sure you want to delete this supplier?"
          onConfirm={confirmDelete}
        />
        <AddSupplierModal
          isOpen={isAddModalOpen}
          toggle={toggleAddModal}
          onSubmit={handleAddSupplier}
        />
      </Container>
    </div>
  );
};

export default Supplier;
